import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Actions, ofType } from '@ngrx/effects';
import { Component, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { UpdateProfileAction, UpdateProfileErrorAction, UpdateProfileSuccessAction } from 'src/app/user/store/user.actions';
import { filter, take, takeUntil, tap } from 'rxjs/operators';

import { InformationalDialogComponent } from '../informational-dialog/informational-dialog.component';
import { Router } from '@angular/router';
import { Sex } from '@bciti/types';
import { State } from 'src/app/store';
import { formatDateOnly, parseDateForDisplay } from 'src/app/global/utils/date/date.utils';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { profileSelector } from 'src/app/user/store/user.selectors';
import { organisationTagSelector } from 'src/app/global/store/global.selectors';

export enum ErrorSeverity {
    Info = 'info',
    Warning = 'warning',
    Error = 'error',
    Critical = 'critical',
}

@Component({
    selector: 'complete-profile-dialog',
    templateUrl: './complete-profile-dialog.component.html',
    styleUrls: ['./complete-profile-dialog.component.scss'],
})
export class CompleteProfileDialogComponent implements OnInit {
    private destroy$ = new Subject();

    public title: string;
    public body: string;
    public profileForm: FormGroup;
    public profileLoaded = false;
    public isLoading = false;
    public maxDate = new Date();
    public t: any = false;
    public genders: { label: string; value: string }[] = [];

    constructor(
        private actions$: Actions,
        private store: Store<State>,
        private formBuilder: FormBuilder,
        public dialogRef: MatDialogRef<CompleteProfileDialogComponent>,
        public translate: TranslateService,
        private dialog: MatDialog,
        private router: Router,
    ) {}

    get sex(): AbstractControl {
        return this.profileForm.get('sex');
    }
    get birthDate(): AbstractControl {
        return this.profileForm.get('birthDate');
    }
    get address(): AbstractControl {
        return this.profileForm.get('address');
    }

    public validateDateIsInPast(c: FormControl): { [key: string]: { [key: string]: boolean } } {
        if (c.value) {
            const today = new Date().toISOString();
            return new Date(c.value).toISOString() < today ? null : { validateDateIsInPast: { valid: false } };
        }
        return null;
    }

    ngOnInit(): void {
        this.genders = [
            {
                label: this.translate.instant('pages.profile.male'),
                value: Sex.Male,
            },
            {
                label: this.translate.instant('pages.profile.female'),
                value: Sex.Female,
            },
            {
                label: this.translate.instant('pages.profile.other'),
                value: Sex.Other,
            },
        ];

        this.store
            .select(organisationTagSelector)
            .pipe(
                takeUntil(this.destroy$),
                filter((orgTag) => !!orgTag),
                take(1),
                tap((orgTag) => {
                    if (orgTag === 'BROSSARD') {
                        this.genders = this.genders.filter((gender) => gender.value !== Sex.Other);
                    }
                }),
            )
            .subscribe();

        this.profileForm = this.formBuilder.group(
            {
                sex: new FormControl('', [Validators.required]),
                birthDate: new FormControl('', [Validators.required, this.validateDateIsInPast]),
                address: new FormControl('', [Validators.required]),
            },
            {
                updateOn: 'change',
            },
        );

        this.dialogRef
            .beforeClosed()
            .pipe(
                tap(() => {
                    this.destroy$.next();
                    this.destroy$.complete();
                }),
            )
            .subscribe();

        this.store
            .select(profileSelector)
            .pipe(
                takeUntil(this.destroy$),
                filter((profile) => !!profile),
                tap(() => (this.profileLoaded = true)),
                tap((profile) => {
                    this.birthDate.setValue(parseDateForDisplay(profile.birthDate) || '');
                    this.sex.setValue(profile.sex || '');

                    if (profile.addresses && profile.addresses[0]) {
                        this.address.setValue(profile.addresses[0]);
                    }
                    this.profileForm.markAsPristine();
                }),
            )
            .subscribe();

        this.actions$
            .pipe(
                takeUntil(this.destroy$),
                ofType(UpdateProfileAction),
                tap(() => (this.isLoading = true)),
            )
            .subscribe();

        this.actions$
            .pipe(
                takeUntil(this.destroy$),
                ofType(UpdateProfileSuccessAction),
                tap(() => (this.isLoading = false)),
                tap(() => this.markAllAsUntouched()),
                tap(() => {
                    this.dialogRef.close();

                    const dialogRef = this.dialog.open(InformationalDialogComponent, {
                        data: {
                            title: this.translate.instant('common.success'),
                            body: this.translate.instant('common.complete-profile-dialog.saveSuccess'),
                        },
                        panelClass: 'informational-dialog',
                    });

                    dialogRef
                        .afterClosed()
                        .pipe(take(1))
                        .subscribe(() => {
                            this.router.navigateByUrl('/consultations');
                        });
                }),
            )
            .subscribe();

        this.actions$
            .pipe(
                takeUntil(this.destroy$),
                ofType(UpdateProfileErrorAction),
                tap(() => (this.isLoading = false)),
                tap(({ error }) => {
                    this.dialogRef.close();

                    this.dialog.open(InformationalDialogComponent, {
                        data: {
                            title: this.translate.instant('common.errors.somethingWentWrong'),
                            body: this.translate.instant('common.complete-profile-dialog.saveError'),
                            errorSeverity: ErrorSeverity.Warning,
                        },
                        panelClass: 'informational-dialog',
                    });
                }),
            )
            .subscribe();

        this.translate
            .get('pages.profile')
            .pipe(takeUntil(this.destroy$))
            .subscribe((t) => (this.t = t));
    }

    async save() {
        this.profileForm.markAllAsTouched();

        if (this.profileForm.valid) {
            this.store.dispatch(
                UpdateProfileAction({
                    payload: {
                        sex: this?.sex?.value.value,
                        birthDate: formatDateOnly(this.birthDate?.value),
                        addresses: this?.address?.value ? [this?.address?.value] : [],
                    },
                }),
            );
        }
    }

    selectGender(e: Sex): void {
        if (e) {
            this.sex.setValue(e);
        }
    }

    public markAllAsUntouched(): void {
        this?.sex.markAsUntouched();
        this?.birthDate.markAsUntouched();
        this?.address.markAsUntouched();
    }

    close(): void {
        this.dialogRef.close();
    }
}
